import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  X,
  UserRound,
  Phone,
  Mail,
  MapPin,
} from "lucide-react";

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  whatsapp: "",
  city: "",
  state: "",
  country: "India",
  notes: "",
};

export default function CustomersManager() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const loadCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/customers");
      setCustomers(Array.isArray(res.data) ? res.data : res.data.items || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (customer) => {
    setEditing(customer);
    setForm({
      name: customer.name || "",
      email: customer.email || "",
      phone: customer.phone || "",
      whatsapp: customer.whatsapp || "",
      city: customer.city || "",
      state: customer.state || "",
      country: customer.country || "India",
      notes: customer.notes || "",
    });
    setShowForm(true);
  };

  const closeForm = () => {
    if (saving) return;
    setShowForm(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const saveCustomer = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      toast.error("Customer name is required");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        whatsapp: form.whatsapp.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        country: form.country.trim() || "India",
        notes: form.notes.trim() || null,
      };

      if (editing?.id) {
        await api.put(`/customers/${editing.id}`, payload);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", payload);
        toast.success("Customer added");
      }

      closeForm();
      await loadCustomers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const deleteCustomer = async (customer) => {
    const ok = window.confirm(
      `Delete customer "${customer.name || "this customer"}"?`
    );

    if (!ok) return;

    try {
      await api.delete(`/customers/${customer.id}`);
      toast.success("Customer deleted");
      await loadCustomers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const filteredCustomers = customers.filter((customer) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;

    return [
      customer.name,
      customer.email,
      customer.phone,
      customer.whatsapp,
      customer.city,
      customer.state,
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(q));
  });

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-4xl text-[#2A1808]">Customers</h1>
          <p className="text-sm text-[#6D5945] mt-1">
            Manage your customer database.
          </p>
        </div>

        <button
          onClick={openCreate}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-[#F49E1A] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#D88708] transition-colors"
        >
          <Plus size={17} />
          Add Customer
        </button>
      </div>

      <div className="bg-white rounded-xl border border-[#E8DCCB] shadow-sm overflow-hidden">
        <div className="p-4 border-b border-[#E8DCCB]">
          <div className="relative max-w-md">
            <Search
              size={17}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B7761]"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customers..."
              className="w-full rounded-md border border-[#DCCDBA] bg-[#FFFDF9] py-2.5 pl-9 pr-3 text-sm outline-none focus:border-[#F49E1A]"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-[#7A6752]">
            Loading customers...
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="p-12 text-center">
            <UserRound className="mx-auto mb-3 text-[#B9A894]" size={38} />
            <p className="text-[#6D5945]">
              {search ? "No customers found." : "No customers yet."}
            </p>

            {!search && (
              <button
                onClick={openCreate}
                className="mt-4 text-sm font-medium text-[#C47700] hover:underline"
              >
                Add your first customer
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FFF7EC] text-[#6D5945]">
                <tr>
                  <th className="text-left px-5 py-3 font-medium">Customer</th>
                  <th className="text-left px-5 py-3 font-medium">Contact</th>
                  <th className="text-left px-5 py-3 font-medium">Location</th>
                  <th className="text-right px-5 py-3 font-medium">Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredCustomers.map((customer) => (
                  <tr
                    key={customer.id}
                    className="border-t border-[#EEE4D7] hover:bg-[#FFFDF9]"
                  >
                    <td className="px-5 py-4">
                      <div className="font-medium text-[#2A1808]">
                        {customer.name || "Unnamed"}
                      </div>
                    </td>

                    <td className="px-5 py-4">
                      <div className="space-y-1 text-[#6D5945]">
                        {customer.phone && (
                          <div className="flex items-center gap-2">
                            <Phone size={14} />
                            {customer.phone}
                          </div>
                        )}

                        {customer.email && (
                          <div className="flex items-center gap-2">
                            <Mail size={14} />
                            {customer.email}
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="px-5 py-4 text-[#6D5945]">
                      {(customer.city || customer.state) && (
                        <div className="flex items-center gap-2">
                          <MapPin size={14} />
                          {[customer.city, customer.state]
                            .filter(Boolean)
                            .join(", ")}
                        </div>
                      )}
                    </td>

                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openEdit(customer)}
                          className="p-2 rounded-md hover:bg-[#FFF1D8] text-[#765329]"
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>

                        <button
                          onClick={() => deleteCustomer(customer)}
                          className="p-2 rounded-md hover:bg-red-50 text-red-600"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-[#E8DCCB] px-6 py-4">
              <h2 className="font-display text-2xl text-[#2A1808]">
                {editing ? "Edit Customer" : "Add Customer"}
              </h2>

              <button
                onClick={closeForm}
                className="rounded-md p-2 text-[#6D5945] hover:bg-[#FFF7EC]"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={saveCustomer} className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label="Name *"
                  value={form.name}
                  onChange={(v) => updateField("name", v)}
                />

                <Field
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={(v) => updateField("email", v)}
                />

                <Field
                  label="Phone"
                  value={form.phone}
                  onChange={(v) => updateField("phone", v)}
                />

                <Field
                  label="WhatsApp"
                  value={form.whatsapp}
                  onChange={(v) => updateField("whatsapp", v)}
                />

                <Field
                  label="City"
                  value={form.city}
                  onChange={(v) => updateField("city", v)}
                />

                <Field
                  label="State"
                  value={form.state}
                  onChange={(v) => updateField("state", v)}
                />

                <Field
                  label="Country"
                  value={form.country}
                  onChange={(v) => updateField("country", v)}
                />

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-[#4A3520] mb-1.5">
                    Notes
                  </label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => updateField("notes", e.target.value)}
                    rows={4}
                    className="w-full rounded-md border border-[#DCCDBA] bg-[#FFFDF9] px-3 py-2.5 text-sm outline-none focus:border-[#F49E1A]"
                    placeholder="Customer notes..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={closeForm}
                  className="rounded-md border border-[#DCCDBA] px-4 py-2.5 text-sm text-[#5D4935] hover:bg-[#FFF7EC]"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-md bg-[#F49E1A] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#D88708] disabled:opacity-60"
                >
                  {saving ? "Saving..." : editing ? "Update Customer" : "Save Customer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, type = "text", value, onChange }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#4A3520] mb-1.5">
        {label}
      </label>

      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-[#DCCDBA] bg-[#FFFDF9] px-3 py-2.5 text-sm outline-none focus:border-[#F49E1A]"
      />
    </div>
  );
}
